/**
 * Худалдан авагчийн БАРИМТ — агуулга НЭГ газар, хэвлэх арга хоёр:
 *  · POS — Sunmi-гийн тууз принтер, `receiptText` (58 мм, 32 тэмдэгт)
 *  · Вэбийн касс — браузерын хэвлэлт, `receiptRows`-ийг HTML болгоно
 * Хоёр цаас ижил мөрүүдтэй байхын тулд аль аль нь `receiptRows`-оос.
 *
 * ⚠️ И-БАРИМТ БИШ. Татварын систем рүү юу ч илгээхгүй («Хариу хүлээж
 *    буй асуултууд» №1). Энэ нь худалдан авагчид өгөх цаас: юу худалдаж
 *    авсан, баталгаа нь хэзээ дуусах, аль салбараас авсан.
 *
 * ⚠️ Мөрийн загвар, размер `order_lines.snapshot`-оос, `items`-ээс БИШ
 *    (CLAUDE.md дүрэм 5) — гутал дахин зарагдсан ч хуучин баримт
 *    зарагдсан үеийн утгаа хэвээр хэвлэнэ.
 */
import { formatDate, formatDateTime, formatMnt, formatSerialDisplay, formatSize } from './format.js';
import { installmentSchedule } from './installment.js';
import { RECEIPT_RULE, receiptRow } from './receipt-text.js';
import { PAYMENT_MN } from './sales-export.js';
import { LEGACY_CREDIT_FEE_LABEL, VAT_PCT } from './surcharge.js';
/**
 * Төлбөрийн нэр — зээлийн апп бол АППЫН нэр («LendMN»), бусад нь
 * хэлбэрийн нэр («Бэлэн»). «Зээлийн апп» гэж бичих нь худалдан
 * авагчид ямар ч мэдээлэл өгөхгүй.
 */
export function receiptPaymentLabel(p) {
    return p.provider?.labelMn ?? PAYMENT_MN[p.method] ?? p.method;
}
export function receiptFromOrder(order) {
    const loan = order.installment;
    return {
        code: order.code,
        storeName: order.store?.name ?? '',
        sellerName: order.soldBy?.fullName ?? order.soldBy?.username ?? '',
        soldAt: order.soldAt,
        totalMnt: order.totalMnt,
        creditFeeMnt: order.creditFeeMnt,
        vatMnt: order.vatMnt,
        customerPhone: order.customer?.phone ?? null,
        payments: order.payments.map((p) => ({ label: receiptPaymentLabel(p), amountMnt: p.amountMnt })),
        // Хуваарь сервертэй ИЖИЛ функцээр (`installmentSchedule`)
        installment: loan
            ? {
                termMonths: loan.termMonths,
                schedule: installmentSchedule(loan.principalMnt, loan.termMonths, new Date(loan.openedAt)),
            }
            : null,
        lines: order.lines.map((l) => ({
            serial: l.snapshot?.serial ?? null,
            title: [l.snapshot?.modelName, l.snapshot?.size ? formatSize(l.snapshot.size) : null]
                .filter(Boolean)
                .join(' · '),
            priceMnt: l.priceMnt,
            discountMnt: l.discountMnt,
            warrantyDays: l.warrantyDays,
            warrantyEndsAt: l.warrantyEndsAt,
        })),
    };
}
const text = (t) => ({ kind: 'text', text: t });
const pair = (label, value, strong = false) => ({ kind: 'pair', label, value, strong });
const rule = { kind: 'rule' };
export function receiptRows(r) {
    const out = [
        text('ЦААТАН ГУТАЛ'),
        text(r.storeName),
        rule,
        pair('Баримт', r.code),
        pair('Огноо', formatDateTime(r.soldAt)),
        pair('Худалдагч', r.sellerName),
        ...(r.customerPhone ? [pair('Утас', r.customerPhone)] : []),
        rule,
    ];
    for (const l of r.lines) {
        if (l.serial)
            out.push(text(formatSerialDisplay(l.serial)));
        out.push(text(l.title));
        out.push(pair('Үнэ', formatMnt(l.priceMnt)));
        if (Number(l.discountMnt) > 0)
            out.push(pair('Хөнгөлөлт', `-${formatMnt(l.discountMnt)}`));
        out.push(pair('Баталгаа', `${Math.round(l.warrantyDays / 365)} жил`));
        out.push(pair('Дуусах', formatDate(l.warrantyEndsAt)));
        out.push({ kind: 'gap' });
    }
    // Нэмэгдэл НИЙТ-ийн дээр — худалдан авагч мөрүүдийн нийлбэр ба
    // НИЙТ хоёрын зөрүүг цаасан дээрээ харж ойлгоно.
    if (Number(r.creditFeeMnt) > 0)
        out.push(pair(LEGACY_CREDIT_FEE_LABEL, formatMnt(r.creditFeeMnt)));
    if (Number(r.vatMnt) > 0)
        out.push(pair(`И-баримт +${VAT_PCT}%`, formatMnt(r.vatMnt)));
    out.push(rule, pair('НИЙТ', formatMnt(r.totalMnt), true));
    for (const p of r.payments)
        out.push(pair(p.label, formatMnt(p.amountMnt)));
    if (r.installment) {
        out.push(rule, text(`Хувь зээл · ${r.installment.termMonths} сар`));
        for (const d of r.installment.schedule)
            out.push(pair(`${d.no}. ${formatDate(d.dueDate)}`, formatMnt(d.amountMnt)));
    }
    out.push(rule);
    return out;
}
/** Sunmi-гийн 58 мм тууз — мөр бүр ≤ 32 тэмдэгт. */
export function receiptText(r) {
    return receiptRows(r)
        .map((row) => {
        switch (row.kind) {
            case 'text': return row.text;
            case 'pair': return receiptRow(row.label, row.value);
            case 'rule': return RECEIPT_RULE;
            case 'gap': return '';
        }
    })
        .join('\n');
}
//# sourceMappingURL=receipt.js.map